import {
	initialize,
	type LDFlagValue,
	type LDClient,
	type LDContext,
	type LDMultiKindContext,
	type LDContextCommon,
} from 'launchdarkly-js-client-sdk';
import { makeAutoObservable } from 'mobx';

import { LAUNCH_DARKLY_KEY } from './Environment';
import {
	type AllFeatureFlags,
	ALL_FEATURE_FLAGS_SCHEMA,
	type CoBrandingLogoSetup,
	DEFAULT_CO_BRANDING_LOGO_OFFSET_IN_PX,
	DEFAULT_CO_BRANDING_LOGO_ZOOM,
	type HeaderWidget,
} from './FeatureFlagSchema';
import {
	DEFAULT_TERMINOLOGY_VARIANT,
	type PointOfCareTerminologyVariant,
} from './terminology/DiagnosesTerminologyModel';

import type { FeatureFlagName } from './FeatureFlagNames';

const LAUNCHDARKLY_MULTI_USER_SEGMENT_KEY = 'user';
const LAUNCHDARKLY_MULTI_PATIENT_SEGMENT_KEY = 'patient';
const LAUNCHDARKLY_MULTI_ACCOUNT_SEGMENT_KEY = 'account';
const LAUNCHDARKLY_MULTI_GROUP_PRACTICE_SEGMENT_KEY = 'group_practice';

const _LAUNCHDARKLY_CONTEXT_KINDS = [
	LAUNCHDARKLY_MULTI_USER_SEGMENT_KEY,
	LAUNCHDARKLY_MULTI_PATIENT_SEGMENT_KEY,
	LAUNCHDARKLY_MULTI_ACCOUNT_SEGMENT_KEY,
	LAUNCHDARKLY_MULTI_GROUP_PRACTICE_SEGMENT_KEY,
] as const;
type LaunchDarklyContextKind = (typeof _LAUNCHDARKLY_CONTEXT_KINDS)[number];

type UserKindContext = LDContextCommon & Required<Pick<LDContextCommon, 'key'>>;
type PatientKindContext = LDContextCommon & Required<Pick<LDContextCommon, 'key'>>;

function withKindContext<K extends string>(
	contextName: K,
	context: LDContextCommon,
): { readonly [P in K]: LDContextCommon };

function withKindContext(contextName: string, context: LDContextCommon) {
	return {
		[contextName]: context,
	} as const;
}

function withUserKindContext(context: UserKindContext) {
	return withKindContext(LAUNCHDARKLY_MULTI_USER_SEGMENT_KEY, context);
}

type OnFlagsInitializedCallbacks = (initializedClient: LDClient) => void;
type OnFlagsFailedCallbacks = (error: Error) => void;

interface InitParams {
	readonly id: string;
	readonly email: string;
	readonly custom: LDContext['custom'];
}

class FeatureFlag {
	private onFlagsInitializedCallbacks: ReadonlyArray<OnFlagsInitializedCallbacks> = [];
	private onFlagsFailedCallbacks: ReadonlyArray<OnFlagsFailedCallbacks> = [];
	client: LDClient | null = null;
	flagsAreInitialized = false;

	private contexts: Partial<Record<LaunchDarklyContextKind, LDContextCommon>> = {};
	private maybeAllFlags: AllFeatureFlags | null = null;

	private constructor() {
		makeAutoObservable(this);
	}

	static readonly create = (): FeatureFlag => new FeatureFlag();

	private readonly setOnFlagsInitializedCallbacks = (callbacks: ReadonlyArray<OnFlagsInitializedCallbacks>): void => {
		this.onFlagsInitializedCallbacks = callbacks;
	};

	private readonly resetOnFlagsInitializedCallbacks = (): void => {
		this.setOnFlagsInitializedCallbacks([]);
	};

	private readonly addOnFlagsInitializedCallback = (callback: OnFlagsInitializedCallbacks): void => {
		this.setOnFlagsInitializedCallbacks([...this.onFlagsInitializedCallbacks, callback]);
	};

	private readonly setOnFlagsFailedCallbacks = (callbacks: ReadonlyArray<OnFlagsFailedCallbacks>): void => {
		this.onFlagsFailedCallbacks = callbacks;
	};

	private readonly resetOnFlagsFailedCallbacks = (): void => {
		this.setOnFlagsFailedCallbacks([]);
	};

	private readonly addOnFlagsFailedCallback = (callback: OnFlagsFailedCallbacks): void => {
		this.setOnFlagsFailedCallbacks([...this.onFlagsFailedCallbacks, callback]);
	};

	private readonly setContexts = (additionalContexts: typeof this.contexts): void => {
		this.contexts = additionalContexts;
	};

	private readonly setAllFlags = (flags: typeof this.maybeAllFlags): void => {
		this.maybeAllFlags = flags;
	};

	private readonly setAndValidateAllFlags = (flags: unknown): void => {
		const parsedFlags = ALL_FEATURE_FLAGS_SCHEMA.safeParse(flags);

		if (!parsedFlags.success) {
			throw new Error(`Failed to parse feature flags: ${parsedFlags.error.message}`);
		}

		this.setAllFlags(parsedFlags.data);
	};

	private readonly setClient = (client: typeof this.client): void => {
		this.client = client;
	};

	private readonly setFlagsAreInitialized = (value: typeof this.flagsAreInitialized): void => {
		this.flagsAreInitialized = value;
	};

	get initializedClient(): LDClient {
		if (!this.client) {
			throw new Error('FeatureFlag client is not initialized');
		}

		return this.client;
	}

	private readonly setContextLocally = (
		contextName: LaunchDarklyContextKind,
		context: LDContextCommon | undefined,
	): void => {
		const { [contextName]: _currentContext, ...restContexts } = this.contexts;

		this.setContexts({
			...restContexts,
			...(context ? { [contextName]: context } : {}),
		});
	};

	private readonly extendContextLocally = (
		contextName: LaunchDarklyContextKind,
		partialNewContext: LDContextCommon,
	): void => {
		const currentContext = this.contexts[contextName] ?? {};
		const newContext = {
			...currentContext,
			...partialNewContext,
		};

		this.setContextLocally(contextName, newContext);
	};

	readonly updateContextRemotely = (newContext: Omit<LDMultiKindContext, 'kind'>): void => {
		const { initializedClient } = this;

		const contextWithKind = {
			kind: 'multi',
			...newContext,
		} as const satisfies LDMultiKindContext;

		initializedClient.identify(contextWithKind);
	};

	readonly updateContextRemotelyFromLocal = (): void => {
		const { contexts } = this;
		this.updateContextRemotely(contexts);
	};

	private readonly setContextLocallyAndSyncRemotely = (
		contextName: LaunchDarklyContextKind,
		context: LDContextCommon | undefined,
	): void => {
		this.setContextLocally(contextName, context);
		this.updateContextRemotelyFromLocal();
	};

	private readonly extendContextLocallyAndSyncRemotely = (
		contextName: LaunchDarklyContextKind,
		partialNewContext: LDContextCommon,
	): void => {
		this.extendContextLocally(contextName, partialNewContext);
		this.updateContextRemotelyFromLocal();
	};

	public readonly setPatientContext = (patientContext: PatientKindContext): void => {
		this.setContextLocallyAndSyncRemotely(LAUNCHDARKLY_MULTI_PATIENT_SEGMENT_KEY, patientContext);
	};

	public readonly resetPatientContext = (): void => {
		this.setContextLocallyAndSyncRemotely(LAUNCHDARKLY_MULTI_PATIENT_SEGMENT_KEY, undefined);
	};

	public readonly extendUserContext = (partialNewUserContext: Omit<UserKindContext, 'key'>): void => {
		this.extendContextLocallyAndSyncRemotely(LAUNCHDARKLY_MULTI_USER_SEGMENT_KEY, partialNewUserContext);
	};

	readonly init = async ({ id, email, custom }: InitParams): Promise<void> => {
		return new Promise((resolve, reject) => {
			const {
				setAndValidateAllFlags,
				setContextLocally,
				setClient,
				setFlagsAreInitialized,
				resetOnFlagsInitializedCallbacks,
				resetOnFlagsFailedCallbacks,
			} = this;

			const customClone: LDContext['custom'] = { ...custom };

			const { first_name: _firstName, last_name: _lastName, ...rest } = customClone;

			const context = {
				kind: 'multi',
				...withUserKindContext({
					key: id,
					email,
					...rest,
				}),
			} as const satisfies LDMultiKindContext;

			setContextLocally('user', context.user);

			const client = initialize(LAUNCH_DARKLY_KEY, context, {
				bootstrap: 'localStorage',
				useReport: true,
			});

			setClient(client);

			function updateAllFlags(): void {
				setAndValidateAllFlags(client.allFlags());
			}

			client.on('initialized', (): void => {
				setFlagsAreInitialized(true);

				for (const callback of this.onFlagsInitializedCallbacks) {
					callback(client);
				}
				resetOnFlagsInitializedCallbacks();
				resetOnFlagsFailedCallbacks();

				updateAllFlags();

				resolve();
			});

			client.on('failed', (error: unknown) => {
				const failure = new Error('FeatureFlag client failed to initialize', { cause: error });

				for (const callback of this.onFlagsFailedCallbacks) {
					callback(failure);
				}
				resetOnFlagsInitializedCallbacks();
				resetOnFlagsFailedCallbacks();

				reject(failure);
			});

			client.on('change', updateAllFlags);
		});
	};

	readonly whenFlagsInitialized = (): Promise<LDClient> => {
		const { flagsAreInitialized, client } = this;

		if (flagsAreInitialized && client) {
			return Promise.resolve(client);
		}

		return new Promise<LDClient>((resolve, reject) => {
			this.addOnFlagsInitializedCallback(resolve);
			this.addOnFlagsFailedCallback(reject);
		});
	};

	readonly getFeatureValue = <T extends LDFlagValue>(featureName: FeatureFlagName, defaultValue: T): T => {
		const { initializedClient } = this;
		return initializedClient.variation(featureName, defaultValue);
	};

	get allFlags(): AllFeatureFlags {
		if (!this.maybeAllFlags) {
			throw new Error('Feature flags are not initialized');
		}

		return this.maybeAllFlags;
	}

	get headerWidgets(): ReadonlyArray<HeaderWidget> {
		const key = 'configuration_CoreServices_PatientPortraitHeaderWidgets' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? [];
	}

	get showFeedbackButton(): boolean {
		const key = 'ShowFeedbackButton' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get coBrandingLogoUrl(): string {
		const key = 'configuration_CoreServices_CoBrandingLogo' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? '';
	}

	get coBrandingLogoSetup(): CoBrandingLogoSetup {
		const key = 'configuration_CoreServices_CoBrandingLogoSetup' as const satisfies keyof AllFeatureFlags;
		const maybeSetup = this.maybeAllFlags?.[key];

		if (maybeSetup) {
			return maybeSetup;
		}

		return {
			whiteLogoPath: this.coBrandingLogoUrl,
			colorLogoPath: '',
			zoom: DEFAULT_CO_BRANDING_LOGO_ZOOM,
			horizontalOffsetInPx: DEFAULT_CO_BRANDING_LOGO_OFFSET_IN_PX,
			verticalOffsetInPx: DEFAULT_CO_BRANDING_LOGO_OFFSET_IN_PX,
		};
	}

	get shouldShowRafBar(): boolean {
		const key = 'configuration_DxCapture_Show_Raf' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? true;
	}

	get portraitWithoutSidActionsEnabled(): boolean {
		const key = 'configuration_DxCapture_portrait_without_sid_actions' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get areActionsDisabled(): boolean {
		const key = 'configuration_DxCapture_disable_actions' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get isYearSwitcherEnabled(): boolean {
		const key = 'configuration_DxCapture_yearSwitcher' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get isPatientWithoutAppointment(): boolean {
		const key = 'configuration_DxCapture_patientWithoutAppointment' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get pointOfCareTerminologyVariant(): PointOfCareTerminologyVariant {
		const key = 'configuration_DxCapture_TerminologyVariant' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? DEFAULT_TERMINOLOGY_VARIANT;
	}

	get showAuditLog(): boolean {
		const key = 'configuration_showAuditLog' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get bypassAlwaysShowAsOpenHccGap(): boolean {
		const key = 'flag_DxCapture_BypassAlwaysShowAsOpenHccGap' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get isDualHccEnabled(): boolean {
		const key = 'flag_DxCapture_DualHcc' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get shouldShowTabClinicalStatus(): boolean {
		const key = 'configuration_quality_showTabClinicalStatus' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? true;
	}

	get shouldShowTabCareTeamStatus(): boolean {
		const key = 'configuration_quality_showTabCareTeamStatus' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? true;
	}

	get showStandaloneOrders(): boolean {
		const key = 'configuration_quality_show_standalone_orders' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? true;
	}

	get showSuggestVisitCode(): boolean {
		const key = 'configuration_ambient_suggestVisitCode' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get isAdminMeasuresConfigurationEnabled(): boolean {
		const key = 'flag_AdminMeasuresConfiguration' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get tabsConfigurationEnabled(): boolean {
		const key = 'configuration_ambient_enableTabConfiguration' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get isHccGapCountTagEnabled(): boolean {
		const key = 'flag_DxCapture_HccGapCountTag' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get isEmrSyncHccLevel(): boolean {
		const key = 'EMRSyncHCCLevel' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get shallPushDxNotes(): boolean {
		const key = 'flag_ShallPushDxNotes' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get canAddManualDiagnosis(): boolean {
		const key = 'configuration_DxCapture_canAddManualDiagnosis' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? true;
	}

	get canAddDiagnosisFromDocument(): boolean {
		const key = 'configuration_AddHCCFromDocuments' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? true;
	}

	get canUseNewDocumentViewerExperience(): boolean {
		const key = 'flag_CoreServices_DocumentViewerExperience' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get showReopenedLabelInRafList(): boolean {
		const key = 'configuration_DxCapture_show_reopened_label_in_raf_list' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? true;
	}

	// No `?? providerView`-style default here: IsPushNoteByDefault's fallback depends on caller-local
	// state (which view is active), so callers apply their own default to this raw, possibly-unset read.
	get maybeIsPushNoteByDefault(): boolean | undefined {
		const key = 'IsPushNoteByDefault' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key];
	}

	get isPatientBasedPortraitEnabled(): boolean {
		const key = 'flag_navina_patient_based_portrait' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}
}

let maybeFeatureFlag: FeatureFlag | null = null;

export function getOrCreateFeatureFlag(): FeatureFlag {
	maybeFeatureFlag ??= FeatureFlag.create();

	return maybeFeatureFlag;
}

export async function getOrCreateInitializedFeatureFlag(initParams: InitParams): Promise<FeatureFlag> {
	const featureFlags = getOrCreateFeatureFlag();

	await featureFlags.init(initParams);

	return featureFlags;
}
